import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useWorkOrderData, useWorkOrderLayout, useWorkOrderSelection } from '../../../../context/WorkOrdersContext';
import { WorkOrderCalendarMode } from '../../../../enums/workOrderView';
import { CALENDAR_DAY_KEY_FORMAT } from '../constants';
import type { WorkOrderFilterRule } from '../../../../types/InfrastructureManagerTypes';
import { applyFilters, buildDayIndex, filterBySearch, getCalendarDays, isOrderInRange } from '../workOrderList.utils';
import CalendarHeader from './CalendarHeader';
import MonthView from './MonthView';
import WeekView from './WeekView';
import styles from './WorkOrderCalendar.module.scss';

// Month view only shows pills for days inside the month; week view shows all seven.
const getVisibleRange = (mode: WorkOrderCalendarMode, anchor: Dayjs): [Dayjs, Dayjs] => {
  const unit = mode === WorkOrderCalendarMode.WEEK ? 'week' : 'month';
  return [anchor.startOf(unit), anchor.endOf(unit).startOf('day')];
};

const getTodayKey = () => dayjs().format(CALENDAR_DAY_KEY_FORMAT);

interface WorkOrderCalendarProps {
  searchTerm: string;
  filters: WorkOrderFilterRule[];
}

const WorkOrderCalendar = ({ searchTerm, filters }: WorkOrderCalendarProps) => {
  const { orders } = useWorkOrderData();
  const { selectedId, toggleSelect } = useWorkOrderSelection();
  const { openDetail } = useWorkOrderLayout();

  const [mode, setMode] = useState(WorkOrderCalendarMode.MONTH);
  const [anchor, setAnchor] = useState(() => dayjs().startOf('day'));
  const [prevSelectedId, setPrevSelectedId] = useState(selectedId);
  const [todayKey, setTodayKey] = useState(getTodayKey);

  // One timer to the next midnight keeps the today marker right on a page left open overnight.
  useEffect(() => {
    const msToMidnight = dayjs().add(1, 'day').startOf('day').diff(dayjs());
    const timer = window.setTimeout(() => setTodayKey(getTodayKey()), msToMidnight);
    return () => window.clearTimeout(timer);
  }, [todayKey]);

  const visibleOrders = useMemo(() => applyFilters(filterBySearch(orders, searchTerm), filters), [orders, searchTerm, filters]);
  const [rangeStart, rangeEnd] = useMemo(() => getVisibleRange(mode, anchor), [mode, anchor]);
  const dayIndex = useMemo(
    () => buildDayIndex(visibleOrders, rangeStart, rangeEnd),
    [visibleOrders, rangeStart, rangeEnd],
  );

  // A selection made elsewhere jumps to the order's first day when none of its days are in view.
  if (selectedId !== prevSelectedId) {
    setPrevSelectedId(selectedId);
    const selected = visibleOrders.find((order) => order.id === selectedId);
    const [firstDay] = selected ? getCalendarDays(selected) : [];
    if (selected && firstDay && !isOrderInRange(selected, rangeStart, rangeEnd)) setAnchor(firstDay);
  }

  const handleStep = useCallback(
    (direction: 1 | -1) => {
      setAnchor((prev) => prev.add(direction, mode === WorkOrderCalendarMode.WEEK ? 'week' : 'month'));
    },
    [mode],
  );

  const handleMonthSelect = useCallback((month: number) => setAnchor((prev) => prev.month(month)), []);
  const handleYearSelect = useCallback((year: number) => setAnchor((prev) => prev.year(year)), []);

  const handleModeToggle = useCallback(() => {
    setMode((prev) => (prev === WorkOrderCalendarMode.MONTH ? WorkOrderCalendarMode.WEEK : WorkOrderCalendarMode.MONTH));
  }, []);

  return (
    <div className={styles.workOrderCalendar}>
      <CalendarHeader
        mode={mode}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        onStep={handleStep}
        onMonthSelect={handleMonthSelect}
        onYearSelect={handleYearSelect}
        onModeToggle={handleModeToggle}
      />
      {mode === WorkOrderCalendarMode.MONTH ? (
        <MonthView
          anchor={anchor}
          todayKey={todayKey}
          dayIndex={dayIndex}
          selectedId={selectedId}
          onSelect={toggleSelect}
          onExpand={openDetail}
        />
      ) : (
        <WeekView
          weekStart={rangeStart}
          todayKey={todayKey}
          dayIndex={dayIndex}
          selectedId={selectedId}
          onSelect={toggleSelect}
          onExpand={openDetail}
        />
      )}
    </div>
  );
};

export default memo(WorkOrderCalendar);
